import { useEffect, useState } from 'react'
import { Alert, Link } from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  IReportComponentInstance,
  IInstructionLookUpEntry,
  ReportComponentType,
  ITableComponentInstance,
  ITextComponentInstance,
  IFunctionComponentInstance,
  ITopBarComponentInstance,
  IInstructionComponentInstance,
} from 'src/types/report-designer'
import { TableComponentProperties } from './properties/TableComponentProperties'
import { TextComponentProperties } from './properties/TextComponentProperties'
import { FunctionComponentProperties } from './properties/FunctionComponentProperties'
import { TopBarComponentProperties } from './properties/TopBarComponentProperties'
import { IFormSchemaColumn } from 'src/generated-types/form-schema'
import { FormFieldType, ReportTemplateStatus } from 'src/enums'
import { ReportSettingsPanel } from './properties/ReportSettingsPanel'
import { Settings } from '@mui/icons-material'
import { InstructionComponentProperties } from './properties/InstructionComponentProperties'
import { IReportTemplateWarningDto } from 'src/generated-types/report-template'
import { VersionCompareSection } from './VersionCompareSection'
import {
  PropertyContainer,
  PropertyHeader,
  PropertyTabs,
  PropertyTab,
  PropertyContent,
  ContentPlaceholder,
  WarningMessageContainer,
} from './PropertyConfigurationSection.style'

export interface IPropertyConfigurationSectionProps {
  selectedItem?: IReportComponentInstance | null
  onUpdateItem?: (updated: IReportComponentInstance) => void
  columns?: IFormSchemaColumn[]
  isColumnsLoading: boolean
  templateName?: string
  locations?: number[]
  status?: ReportTemplateStatus
  onTemplateNameChange?: (name: string) => void
  onLocationsChange?: (locations: number[]) => void
  isEditMode?: boolean
  templateId?: number
  customerId?: number
  formSchemaVersionId?: number
  onLookUpChange?: (
    updater: (prev: Record<string, IInstructionLookUpEntry>) => Record<string, IInstructionLookUpEntry>,
  ) => void
  warnings?: IReportTemplateWarningDto
  warningMessage?: string
  onUpgrade?: () => void
  isUpgradeLoading?: boolean
  formSchemaId?: number
}

export const getInstructionColumnNames = (column: IFormSchemaColumn, translate) => {
  if (column.type === FormFieldType.instructionFile) return translate('file', { ns: 'common' })
  if (column.type === FormFieldType.instructionImage) return translate('image', { ns: 'common' })
  if (column.type === FormFieldType.richText) return translate('text', { ns: 'dynamic-report' })
}

export const PropertyConfigurationSection: React.FC<IPropertyConfigurationSectionProps> = ({
  selectedItem,
  onUpdateItem,
  columns,
  isColumnsLoading,
  templateName = '',
  locations = [],
  status,
  onTemplateNameChange,
  onLocationsChange,
  isEditMode,
  templateId,
  customerId,
  formSchemaVersionId,
  onLookUpChange,
  warnings,
  warningMessage = '',
  onUpgrade,
  isUpgradeLoading = false,
  formSchemaId,
}) => {
  const { t: translate } = useTranslation(['dynamic-report', 'common'])
  const [activeTab, setActiveTab] = useState(0)
  const [showFullWarning, setShowFullWarning] = useState(false)
  const instructionColumns = [FormFieldType.instructionFile, FormFieldType.instructionImage, FormFieldType.richText]
  const [filteredColumns, setFilteredColumns] = useState<IFormSchemaColumn[]>(
    columns?.filter(col => !instructionColumns.includes(col.type) && col.type != FormFieldType.title) ?? [],
  )
  const functionColumnsTypes = [FormFieldType.textField, FormFieldType.numberField]
  const [functionColumns, setFunctionColumns] = useState<IFormSchemaColumn[]>(
    filteredColumns.filter(col => functionColumnsTypes.includes(col.type)),
  )

  const [instructionColumnSet, setInstructionColumnSet] = useState<IFormSchemaColumn[]>(
    columns
      ?.filter(col => instructionColumns.includes(col.type))
      .map(col => ({
        name: getInstructionColumnNames(col, translate) || col.name,
        identifier: col.identifier,
        type: col.type,
        content: col.content,
      })) ?? [],
  )

  useEffect(() => {
    setFilteredColumns(
      columns?.filter(col => !instructionColumns.includes(col.type) && col.type !== FormFieldType.title) ?? [],
    )
    setInstructionColumnSet(
      columns
        ?.filter(col => instructionColumns.includes(col.type))
        .map(col => ({
          name: getInstructionColumnNames(col, translate) || col.name,
          identifier: col.identifier,
          type: col.type,
          content: col.content,
        })) ?? [],
    )
  }, [columns, translate])

  useEffect(() => {
    setFunctionColumns(filteredColumns.filter(col => functionColumnsTypes.includes(col.type)))
  }, [filteredColumns])

  useEffect(() => {
    if (selectedItem) {
      setActiveTab(0)
    }
  }, [selectedItem])

  const renderProperties = () => {
    if (!selectedItem || !onUpdateItem) {
      return <ContentPlaceholder>{translate('property-select-hint', { ns: 'dynamic-report' })}</ContentPlaceholder>
    }

    switch (selectedItem.type) {
      case ReportComponentType.TopBar:
        return <TopBarComponentProperties instance={selectedItem as ITopBarComponentInstance} onChange={onUpdateItem} />
      case ReportComponentType.DataTable:
        return (
          <TableComponentProperties
            instance={selectedItem as ITableComponentInstance}
            onChange={onUpdateItem}
            columns={filteredColumns}
            isColumnsLoading={isColumnsLoading}
          />
        )
      case ReportComponentType.Text:
        return <TextComponentProperties instance={selectedItem as ITextComponentInstance} onChange={onUpdateItem} />
      case ReportComponentType.Function:
        return (
          <FunctionComponentProperties
            instance={selectedItem as IFunctionComponentInstance}
            onChange={onUpdateItem}
            columns={functionColumns}
            isColumnsLoading={isColumnsLoading}
          />
        )
      case ReportComponentType.Instruction:
        return (
          <InstructionComponentProperties
            instance={selectedItem as IInstructionComponentInstance}
            onChange={onUpdateItem}
            columns={instructionColumnSet}
            isColumnsLoading={isColumnsLoading}
            onLookUpChange={onLookUpChange}
          />
        )

      default:
        return null
    }
  }

  return (
    <PropertyContainer>
      <PropertyHeader>
        <Settings sx={{ fontSize: '20px', marginRight: 1, color: 'text.secondary' }} />
        <PropertyTabs value={activeTab} onChange={(_, val) => setActiveTab(val)} textColor="inherit">
          <PropertyTab label={translate('component', { ns: 'dynamic-report' })} />
          <PropertyTab label={translate('report', { ns: 'dynamic-report' })} />
        </PropertyTabs>
      </PropertyHeader>
      <PropertyContent>
        {warningMessage && (
          <Alert severity="warning" sx={{ mb: 2, fontSize: '12px' }}>
            <WarningMessageContainer activeTab={activeTab} showFullWarning={showFullWarning}>
              {warningMessage}
            </WarningMessageContainer>
            {activeTab === 0 && (
              <Link
                component="button"
                underline="hover"
                onClick={() => setShowFullWarning(prev => !prev)}
                sx={{ fontSize: '11px', mt: 0.5, display: 'block' }}
              >
                {showFullWarning ? translate('showLess', { ns: 'common' }) : translate('showMore', { ns: 'common' })}
              </Link>
            )}
          </Alert>
        )}
        {activeTab === 0 && renderProperties()}
        {activeTab === 1 && onTemplateNameChange && onLocationsChange && (
          <>
            {warnings && <VersionCompareSection warnings={warnings} />}
            <ReportSettingsPanel
              templateName={templateName}
              locations={locations}
              status={status}
              onTemplateNameChange={onTemplateNameChange}
              onLocationsChange={onLocationsChange}
              isEditMode={isEditMode}
              templateId={templateId}
              customerId={customerId}
              formSchemaVersionId={formSchemaVersionId}
              onUpgrade={onUpgrade}
              isUpgradeLoading={isUpgradeLoading}
              warnings={warnings}
              formSchemaId={formSchemaId}
            />
          </>
        )}
      </PropertyContent>
    </PropertyContainer>
  )
}
